import { useState, useEffect, useRef } from 'react';
import { User, LayoutDashboard, Phone, Menu, X, Calendar, ChevronDown, ExternalLink, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from './ui/Avatar';
import { User as FirebaseUser } from 'firebase/auth';
import { BrandLogo } from './BrandLogo';
import type { NavItem, HeaderConfig, CTAButton } from '@/lib/cms';
import { NavIconRenderer } from '@/lib/nav-icons';
import { replaceCmsPlaceholders, type CMSPlaceholderReplacements } from '@/lib/cms-placeholders';

interface BrandingInfo {
  brandName?: string;
  brandTagline?: string;
}

/** Contact info from app settings (optional). */
interface ContactInfo {
  contactPhone?: string;
  contactEmail?: string;
  companyAddress?: string;
  /** Email to receive Strategy Call form notifications. */
  notificationEmail?: string;
}

function phoneToTelHref(displayPhone: string): string {
  const digits = displayPhone.replace(/\D/g, '');
  return digits ? `tel:+${digits}` : 'tel:';
}

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface HeaderProps {
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  onProfileClick: () => void;
  branding?: BrandingInfo;
  contact?: ContactInfo;
  isAdmin?: boolean;
  /** When true, render links and buttons as non-clickable spans (e.g. for CMS preview). */
  linksDisabled?: boolean;
  /** Opens the Book a Strategy Call modal. When provided, header CTA uses it instead of in-header state. */
  onOpenStrategyCall?: () => void;
  /** Dynamic nav items from CMS (header placement). */
  navItems?: NavItem[];
  /** Header config from CMS (overrides branding/contact defaults per-header). */
  headerConfig?: HeaderConfig | null;
  /** If set, renders this raw HTML instead of the default React header. */
  headerHtml?: string | null;
  /** For substituting {{phone-number}}, etc. in nav item HTML snippets. */
  placeholderReplacements?: CMSPlaceholderReplacements | null;
}

// ── Nav item renderer (desktop) ────────────────────────────────────────────

interface NavLinkProps {
  item: NavItem;
  children?: NavItem[];
  linksDisabled?: boolean;
  onClick?: () => void;
  placeholderReplacements?: CMSPlaceholderReplacements | null;
}

function substituteEmbedHtml(html: string, placeholderReplacements?: CMSPlaceholderReplacements | null): string {
  return placeholderReplacements ? replaceCmsPlaceholders(html, placeholderReplacements) : html;
}

const DesktopNavLink = ({ item, children = [], linksDisabled, onClick, placeholderReplacements }: NavLinkProps) => {
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!dropOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  if (!item.isVisible) return null;

  if (item.type === 'embed' && item.embedCode) {
    return (
      <span
        className="inline-flex items-center"
        dangerouslySetInnerHTML={{ __html: substituteEmbedHtml(item.embedCode, placeholderReplacements) }}
      />
    );
  }
  if (item.type === 'html' && item.embedCode) {
    return (
      <span
        className="inline-flex items-center text-sm text-slate-600"
        dangerouslySetInnerHTML={{ __html: substituteEmbedHtml(item.embedCode, placeholderReplacements) }}
      />
    );
  }
  if (item.type === 'text') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
        {item.icon && <NavIconRenderer name={item.icon} size={14} className="opacity-60" />}
        {item.label}
      </span>
    );
  }

  const href = item.slug || item.url || '#';
  const isExternal = !!(item.url && !item.slug);

  const baseClass = "text-sm font-medium text-slate-700 hover:text-[var(--smb-primary-mid)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] rounded px-1 py-1";

  if (item.type === 'button') {
    const btnStyle = item.buttonStyle ?? 'primary';
    const btnClass = btnStyle === 'primary'
      ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 text-white hover:opacity-95 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] shrink-0'
      : btnStyle === 'secondary'
      ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border border-[var(--smb-primary-mid)] text-[var(--smb-primary-mid)] hover:bg-[var(--smb-primary-mid)] hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 shrink-0'
      : 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 shrink-0';

    if (linksDisabled) {
      return <span className={btnClass} style={btnStyle === 'primary' ? { backgroundColor: 'var(--smb-primary-mid)' } : {}} aria-hidden>{item.label}</span>;
    }
    const handleClick = () => {
      onClick?.();
      if (item.url) {
        if (isExternal) window.open(item.url, item.target ?? '_self');
        else navigate(item.url);
      } else if (item.slug) navigate(item.slug);
    };
    return (
      <button type="button" onClick={handleClick} className={btnClass} style={btnStyle === 'primary' ? { backgroundColor: 'var(--smb-primary-mid)' } : {}}>
        {item.label}
      </button>
    );
  }

  if (item.type === 'dropdown' && children.length > 0) {
    return (
      <div ref={dropRef} className="relative">
        <button
          type="button"
          onClick={() => setDropOpen((o) => !o)}
          className={`${baseClass} inline-flex items-center gap-1.5`}
          aria-expanded={dropOpen}
        >
          {item.icon && <NavIconRenderer name={item.icon} size={14} className="opacity-70" />}
          {item.label}
          <ChevronDown size={13} className={`transition-transform ${dropOpen ? 'rotate-180' : ''}`} />
        </button>
        {dropOpen && (
          <div className="absolute top-full left-0 mt-1 min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
            {children.filter((c) => c.isVisible).map((child) => {
              const childHref = child.slug || child.url || '#';
              const childExternal = !!(child.url && !child.slug);
              return (
                <div key={child.id}>
                  {linksDisabled ? (
                    <span className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 cursor-default">
                      {child.icon && <NavIconRenderer name={child.icon} size={14} className="opacity-60" />}
                      {child.label}
                    </span>
                  ) : childExternal ? (
                    <a
                      href={childHref}
                      target={child.target ?? '_self'}
                      rel="noopener noreferrer"
                      onClick={() => { setDropOpen(false); onClick?.(); }}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {child.icon && <NavIconRenderer name={child.icon} size={14} className="opacity-60" />}
                      {child.label}
                      {child.target === '_blank' && <ExternalLink size={12} className="opacity-50" />}
                    </a>
                  ) : (
                    <Link
                      to={childHref}
                      onClick={() => { setDropOpen(false); onClick?.(); }}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      {child.icon && <NavIconRenderer name={child.icon} size={14} className="opacity-60" />}
                      {child.label}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Standard link
  if (linksDisabled) {
    return (
      <span className={`${baseClass} cursor-default inline-flex items-center gap-1.5`}>
        {item.icon && <NavIconRenderer name={item.icon} size={14} className="opacity-70" />}
        {item.label}
      </span>
    );
  }
  if (isExternal) {
    return (
      <a href={href} target={item.target ?? '_self'} rel="noopener noreferrer" onClick={onClick} className={`${baseClass} inline-flex items-center gap-1.5`}>
        {item.icon && <NavIconRenderer name={item.icon} size={14} className="opacity-70" />}
        {item.label}
        {item.target === '_blank' && <ExternalLink size={12} className="opacity-50" />}
      </a>
    );
  }
  return (
    <Link to={href} onClick={onClick} className={`${baseClass} inline-flex items-center gap-1.5`}>
      {item.icon && <NavIconRenderer name={item.icon} size={14} className="opacity-70" />}
      {item.label}
    </Link>
  );
};

// ── Mobile nav item ────────────────────────────────────────────────────────

const MobileNavItem = ({
  item,
  children = [],
  onClose,
  placeholderReplacements,
}: {
  item: NavItem;
  children?: NavItem[];
  onClose: () => void;
  onOpenStrategyCall?: () => void;
  placeholderReplacements?: CMSPlaceholderReplacements | null;
}) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!item.isVisible) return null;
  if (item.type === 'embed' && item.embedCode) {
    return (
      <span
        className="px-4 py-2 block"
        dangerouslySetInnerHTML={{ __html: substituteEmbedHtml(item.embedCode, placeholderReplacements) }}
      />
    );
  }
  if (item.type === 'html' && item.embedCode) {
    return (
      <div
        className="px-4 py-2 text-sm text-slate-600"
        dangerouslySetInnerHTML={{ __html: substituteEmbedHtml(item.embedCode, placeholderReplacements) }}
      />
    );
  }
  if (item.type === 'text') {
    return (
      <p className="flex items-center gap-2 px-4 py-2 text-sm text-slate-500">
        {item.icon && <NavIconRenderer name={item.icon} size={15} className="opacity-60 shrink-0" />}
        {item.label}
      </p>
    );
  }

  const href = item.slug || item.url || '#';
  const isExternal = !!(item.url && !item.slug);

  const baseClass = "flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]";

  if (item.type === 'button') {
    const btnStyle = item.buttonStyle ?? 'primary';
    const handleClick = () => {
      onClose();
      if (item.url) {
        if (isExternal) window.open(item.url, item.target ?? '_self');
        else navigate(item.url);
      } else if (item.slug) navigate(item.slug);
    };
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 hover:opacity-95 active:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 ${btnStyle === 'primary' ? 'text-white' : btnStyle === 'secondary' ? 'border border-[var(--smb-primary-mid)] text-[var(--smb-primary-mid)]' : 'border border-slate-300 text-slate-700'}`}
        style={btnStyle === 'primary' ? { backgroundColor: 'var(--smb-primary-mid)' } : {}}
      >
        {item.label}
      </button>
    );
  }

  if (item.type === 'dropdown' && children.length > 0) {
    return (
      <div>
        <button type="button" onClick={() => setOpen((o) => !o)} className={`${baseClass} justify-between`}>
          <span className="flex items-center gap-2">
            {item.icon && <NavIconRenderer name={item.icon} size={16} className="opacity-70" />}
            {item.label}
          </span>
          <ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="ml-4 mt-1 space-y-0.5">
            {children.filter((c) => c.isVisible).map((child) => {
              const childHref = child.slug || child.url || '#';
              const childExternal = !!(child.url && !child.slug);
              if (childExternal) {
                return (
                  <a key={child.id} href={childHref} target={child.target ?? '_self'} rel="noopener noreferrer" onClick={onClose} className={baseClass}>
                    {child.icon && <NavIconRenderer name={child.icon} size={16} className="opacity-60" />}
                    {child.label}
                    {child.target === '_blank' && <ExternalLink size={12} className="opacity-50 ml-auto" />}
                  </a>
                );
              }
              return (
                <Link key={child.id} to={childHref} onClick={onClose} className={baseClass}>
                  {child.icon && <NavIconRenderer name={child.icon} size={16} className="opacity-60" />}
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (isExternal) {
    return (
      <a href={href} target={item.target ?? '_self'} rel="noopener noreferrer" onClick={onClose} className={`${baseClass} justify-between`}>
        <span className="flex items-center gap-2">
          {item.icon && <NavIconRenderer name={item.icon} size={16} className="opacity-70" />}
          {item.label}
        </span>
        {item.target === '_blank' && <ExternalLink size={12} className="opacity-50 ml-auto" />}
      </a>
    );
  }
  return (
    <Link to={href} onClick={onClose} className={baseClass}>
      {item.icon && <NavIconRenderer name={item.icon} size={16} className="opacity-70" />}
      {item.label}
    </Link>
  );
};

// ── CTA Button renderer (from HeaderConfig) ────────────────────────────────

const CTAButtonRenderer = ({
  btn,
  linksDisabled,
  onOpenStrategyCall,
  mobile = false,
  onClick,
}: {
  btn: CTAButton;
  linksDisabled?: boolean;
  onOpenStrategyCall?: () => void;
  mobile?: boolean;
  onClick?: () => void;
}) => {
  const navigate = useNavigate();
  if (!btn.isVisible) return null;

  const btnStyle = btn.style ?? 'primary';
  const baseDesktop = btnStyle === 'primary'
    ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 text-white hover:opacity-95 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] shrink-0'
    : btnStyle === 'secondary'
    ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border border-[var(--smb-primary-mid)] text-[var(--smb-primary-mid)] hover:bg-[var(--smb-primary-mid)] hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 shrink-0'
    : 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-100 transition-all duration-200 focus:outline-none shrink-0';
  const baseMobile = `flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-base font-semibold transition-all duration-200 hover:opacity-95 active:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 ${btnStyle === 'primary' ? 'text-white' : btnStyle === 'secondary' ? 'border border-[var(--smb-primary-mid)] text-[var(--smb-primary-mid)]' : 'border border-slate-300 text-slate-700'}`;

  const cls = mobile ? baseMobile : baseDesktop;
  const style = btnStyle === 'primary' ? { backgroundColor: 'var(--smb-primary-mid)' } : {};

  if (linksDisabled) {
    return <span className={cls} style={style} aria-hidden>{btn.label}</span>;
  }

  const handleClick = () => {
    onClick?.();
    if (btn.action === 'open-strategy-call') {
      onOpenStrategyCall?.();
    } else if (btn.url) {
      if (btn.action === 'external') window.open(btn.url, '_blank');
      else navigate(btn.url);
    }
  };

  return (
    <button type="button" onClick={handleClick} className={cls} style={style}>
      {btn.label}
    </button>
  );
};

// ── Main Header component ──────────────────────────────────────────────────

export const Header = ({
  user,
  userProfile,
  onProfileClick,
  branding,
  contact,
  isAdmin = false,
  linksDisabled = false,
  onOpenStrategyCall,
  navItems = [],
  headerConfig = null,
  headerHtml = null,
  placeholderReplacements = null,
}: HeaderProps): JSX.Element => {
  // ── All hooks must be declared before any early return ──────────────────
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropOpen, setProfileDropOpen] = useState(false);
  const profileDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!profileDropOpen) return;
    const handler = (e: MouseEvent) => {
      if (profileDropRef.current && !profileDropRef.current.contains(e.target as Node)) {
        setProfileDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileDropOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setProfileDropOpen(false);
      }
    };
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen]);

  // If a custom HTML override is set, render it directly instead of the React template.
  if (headerHtml && headerHtml.trim()) {
    return <div dangerouslySetInnerHTML={{ __html: headerHtml }} />;
  }

  const displayName = userProfile?.displayName || user?.displayName || 'Guest';
  const photoUrl = user?.photoURL || userProfile?.photoUrl || undefined;
  const isAuthenticated = !!(userProfile || user);
  const effectiveBrandName = branding?.brandName || 'SMBPrime';

  // Tagline: headerConfig overrides branding
  const effectiveTagline = headerConfig?.tagline ?? branding?.brandTagline ?? 'Government contracting made simple';

  // Phone: headerConfig overrides contact settings
  const showPhone = headerConfig ? headerConfig.showContactPhone : true;
  const displayPhone = (headerConfig?.contactPhone || contact?.contactPhone) ?? '+1 832-542-6260';
  const telHref = phoneToTelHref(displayPhone);

  // Profile / admin link visibility (from headerConfig, defaults true)
  const showProfileButton = headerConfig ? headerConfig.showProfileButton : true;
  const showAdminLink = headerConfig ? headerConfig.showAdminLink : true;

  // CTA buttons from headerConfig (sorted)
  const ctaButtons = headerConfig
    ? [...headerConfig.ctaButtons].filter((b) => b.isVisible).sort((a, b) => a.order - b.order)
    : null; // null = use legacy hardcoded "Book a Strategy Call"

  const showDashboardLink = !!user && !user.isAnonymous;

  // Shorten display name to first name, capped at 5 characters
  const shortName = (() => {
    const first = displayName.split(' ')[0] ?? displayName;
    return first.length > 5 ? first.slice(0, 5) : first;
  })();

  // Header nav items (top-level, ordered, visible)
  const topNavItems = navItems
    .filter((it) => it.placement === 'header' && !it.parentId && it.isVisible)
    .sort((a, b) => a.order - b.order);

  const childItemsOf = (parentId: string) =>
    navItems.filter((it) => it.parentId === parentId && it.isVisible).sort((a, b) => a.order - b.order);

  const closeMobile = () => setMobileMenuOpen(false);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  // Logo: headerConfig.logoUrl overrides BrandLogo
  const logoElement = headerConfig?.logoUrl ? (
    <img src={headerConfig.logoUrl} alt={effectiveBrandName} className="h-8 sm:h-9 w-auto object-contain" />
  ) : (
    <BrandLogo className="h-8 sm:h-9 w-auto" alt={effectiveBrandName} />
  );

  return (
    <header
      className="shadow-md sticky top-0 z-40 backdrop-blur-sm border-b border-[var(--smb-header-border)]"
      style={{ backgroundColor: 'var(--smb-header-bg)', color: 'var(--smb-ink)' }}
      role="banner"
    >
      {!linksDisabled && (
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:bg-[var(--smb-primary)] focus:text-white focus:ring-[var(--smb-primary-mid)] focus:ring-offset-[var(--smb-header-bg)]"
        >
          Skip to main content
        </a>
      )}

      {/* ── Top bar ─────────────────────────────────────────────── */}
      <nav className="max-w-7xl mx-auto px-4 py-3" aria-label="Main navigation">
        <div className="flex justify-between items-center">

          {/* Logo + tagline */}
          <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
            {linksDisabled ? (
              <span className="flex items-center gap-2 rounded-lg p-1 -m-1 cursor-default" aria-label={`${effectiveBrandName} - Go to home`}>
                <div className="min-w-0">
                  {logoElement}
                  <p className="hidden md:block text-xs truncate text-slate-600">{effectiveTagline}</p>
                </div>
              </span>
            ) : (
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-2 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] rounded-lg p-1 -m-1 text-inherit"
                aria-label={`${effectiveBrandName} - Go to home`}
              >
                <div className="min-w-0">
                  {logoElement}
                  <p className="hidden md:block text-xs truncate text-slate-600">{effectiveTagline}</p>
                </div>
              </Link>
            )}
          </div>

          {/* Desktop right-side controls */}
          <div className="hidden md:flex items-center gap-2 lg:gap-3 flex-shrink-0 flex-wrap justify-end">
            {/* Dynamic nav items from CMS */}
            {topNavItems.map((item) => (
              <DesktopNavLink
                key={item.id}
                item={item}
                children={childItemsOf(item.id)}
                linksDisabled={linksDisabled}
                placeholderReplacements={placeholderReplacements}
              />
            ))}

            {/* Phone number */}
            {showPhone && (
              linksDisabled ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-default" aria-hidden>
                  <Phone size={14} />
                  {displayPhone}
                </span>
              ) : (
                <a
                  href={telHref}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[var(--smb-primary-mid)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] rounded px-1"
                  aria-label={`Call ${effectiveBrandName}: ${displayPhone}`}
                >
                  <Phone size={14} aria-hidden />
                  {displayPhone}
                </a>
              )
            )}

            {/* CTA Buttons — either from headerConfig or the legacy hardcoded button */}
            {ctaButtons !== null ? (
              ctaButtons.map((btn) => (
                <CTAButtonRenderer
                  key={btn.id}
                  btn={btn}
                  linksDisabled={linksDisabled}
                  onOpenStrategyCall={onOpenStrategyCall}
                />
              ))
            ) : (
              linksDisabled ? (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap text-white shrink-0 cursor-default"
                  style={{ backgroundColor: 'var(--smb-primary-mid)' }}
                  aria-hidden
                >
                  Book a Strategy Call
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => onOpenStrategyCall?.()}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 text-white hover:opacity-95 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] shrink-0"
                  style={{ backgroundColor: 'var(--smb-primary-mid)' }}
                  aria-label="Book a free strategy call"
                >
                  Book a Strategy Call
                </button>
              )
            )}

            {/* Profile dropdown — contains Dashboard, Admin, and Profile action */}
            {showProfileButton && (
              linksDisabled ? (
                <span className="flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-full min-w-0 text-slate-700 cursor-default" aria-hidden>
                  <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={16} className="text-slate-600" />
                  </div>
                </span>
              ) : (
                <div ref={profileDropRef} className="relative">
                  <button
                    onClick={() => setProfileDropOpen((o) => !o)}
                    onKeyDown={(e) => handleKeyDown(e, () => setProfileDropOpen((o) => !o))}
                    className="flex items-center gap-1.5 hover:bg-slate-200/80 active:bg-slate-300/80 pl-1.5 pr-1.5 py-1 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] text-slate-800"
                    aria-label={isAuthenticated ? `Open ${displayName}'s profile menu` : 'Open profile menu'}
                    aria-expanded={profileDropOpen}
                    aria-haspopup="menu"
                  >
                    {isAuthenticated ? (
                      <>
                        <Avatar name={displayName} url={photoUrl} size="sm" />
                        <span className="text-sm font-medium text-slate-800 hidden lg:inline">{shortName}</span>
                        <ChevronDown size={13} className={`text-slate-500 transition-transform ${profileDropOpen ? 'rotate-180' : ''}`} aria-hidden />
                      </>
                    ) : (
                      <>
                        <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                          <User size={16} className="text-slate-600" />
                        </div>
                        <ChevronDown size={13} className={`text-slate-500 transition-transform ${profileDropOpen ? 'rotate-180' : ''}`} aria-hidden />
                      </>
                    )}
                  </button>

                  {profileDropOpen && (
                    <div
                      role="menu"
                      className="absolute top-full right-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden"
                    >
                      {/* User identity header */}
                      {isAuthenticated && (
                        <div className="px-4 py-2.5 border-b border-gray-100">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Signed in as</p>
                          <p className="text-sm font-semibold text-slate-800 truncate mt-0.5">{displayName}</p>
                        </div>
                      )}

                      {/* Dashboard */}
                      {showDashboardLink && (
                        <Link
                          to="/dashboard"
                          role="menuitem"
                          onClick={() => setProfileDropOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          aria-label="Open customer dashboard"
                        >
                          <LayoutDashboard size={15} className="opacity-60 shrink-0" aria-hidden />
                          Dashboard
                        </Link>
                      )}

                      {/* Admin panel */}
                      {isAdmin && showAdminLink && (
                        <Link
                          to="/admin"
                          role="menuitem"
                          onClick={() => setProfileDropOpen(false)}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                          aria-label="Open admin panel"
                        >
                          <ShieldCheck size={15} className="opacity-60 shrink-0" aria-hidden />
                          Admin Panel
                        </Link>
                      )}

                      {/* Divider before profile */}
                      {(showDashboardLink || (isAdmin && showAdminLink)) && (
                        <div className="h-px bg-gray-100 my-1" role="separator" />
                      )}

                      {/* Profile / account */}
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setProfileDropOpen(false); onProfileClick(); }}
                        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                        aria-label={isAuthenticated ? `Open ${displayName}'s profile` : 'Sign in / Sign up'}
                      >
                        <User size={15} className="opacity-60 shrink-0" aria-hidden />
                        {isAuthenticated ? 'My Profile' : 'Sign In'}
                      </button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          {/* Mobile right-side: avatar + hamburger */}
          <div className="flex md:hidden items-center gap-2 flex-shrink-0">
            {/* Profile avatar always visible on mobile */}
            {!linksDisabled && showProfileButton && (
              <button
                onClick={onProfileClick}
                onKeyDown={(e) => handleKeyDown(e, onProfileClick)}
                className="flex items-center justify-center hover:bg-slate-200/80 active:bg-slate-300/80 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                aria-label={isAuthenticated ? `Open ${displayName}'s profile` : 'Open profile menu'}
                aria-expanded="false"
              >
                {isAuthenticated ? (
                  <Avatar name={displayName} url={photoUrl} size="sm" />
                ) : (
                  <div className="bg-slate-200 w-9 h-9 rounded-full flex items-center justify-center" aria-hidden="true">
                    <User size={17} className="text-slate-600" />
                  </div>
                )}
              </button>
            )}

            {/* Hamburger toggle */}
            {!linksDisabled && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen((o) => !o)}
                className="flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 hover:bg-slate-200/80 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>

        </div>
      </nav>

      {/* ── Mobile slide-down menu ───────────────────────────────── */}
      {mobileMenuOpen && !linksDisabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 top-[var(--header-height,72px)] bg-black/40 z-30 md:hidden"
            aria-hidden="true"
            onClick={closeMobile}
          />
          {/* Menu panel */}
          <div
            id="mobile-menu"
            role="dialog"
            aria-label="Navigation menu"
            className="absolute top-full left-0 right-0 z-40 md:hidden border-t border-[var(--smb-header-border)] shadow-xl"
            style={{ backgroundColor: 'var(--smb-header-bg)' }}
          >
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">

              {/* Dynamic CMS nav items */}
              {topNavItems.length > 0 && (
                <>
                  {topNavItems.map((item) => (
                    <MobileNavItem
                      key={item.id}
                      item={item}
                      children={childItemsOf(item.id)}
                      onClose={closeMobile}
                      onOpenStrategyCall={onOpenStrategyCall}
                      placeholderReplacements={placeholderReplacements}
                    />
                  ))}
                  <div className="h-px bg-slate-200 my-1" role="separator" />
                </>
              )}

              {/* CTA buttons */}
              {ctaButtons !== null ? (
                ctaButtons.map((btn) => (
                  <CTAButtonRenderer
                    key={btn.id}
                    btn={btn}
                    linksDisabled={linksDisabled}
                    onOpenStrategyCall={onOpenStrategyCall}
                    mobile={true}
                    onClick={closeMobile}
                  />
                ))
              ) : (
                <button
                  type="button"
                  onClick={() => { onOpenStrategyCall?.(); closeMobile(); }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl text-base font-semibold text-white transition-all duration-200 hover:opacity-95 active:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                  style={{ backgroundColor: 'var(--smb-primary-mid)' }}
                  aria-label="Book a free strategy call"
                >
                  <Calendar size={18} aria-hidden />
                  Book a Strategy Call
                </button>
              )}

              {/* Phone */}
              {showPhone && (
                <a
                  href={telHref}
                  onClick={closeMobile}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                  aria-label={`Call ${effectiveBrandName}: ${displayPhone}`}
                >
                  <Phone size={18} className="text-slate-500 shrink-0" aria-hidden />
                  <span>{displayPhone}</span>
                </a>
              )}

              <div className="h-px bg-slate-200 my-1" role="separator" />

              {/* Dashboard (customer) */}
              {showDashboardLink && (
                <Link
                  to="/dashboard"
                  onClick={closeMobile}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                  aria-label="Open customer dashboard"
                >
                  <LayoutDashboard size={18} className="text-slate-500 shrink-0" aria-hidden />
                  <span>Dashboard</span>
                </Link>
              )}

              {/* Admin */}
              {isAdmin && showAdminLink && (
                <Link
                  to="/admin"
                  onClick={closeMobile}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)]"
                  aria-label="Open admin panel"
                >
                  <LayoutDashboard size={18} className="text-slate-500 shrink-0" aria-hidden />
                  <span>Admin</span>
                </Link>
              )}

              {/* Profile / Sign in */}
              {showProfileButton && (
                <button
                  onClick={() => { onProfileClick(); closeMobile(); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--smb-primary-mid)] focus:ring-offset-2 focus:ring-offset-[var(--smb-header-bg)] text-left"
                  aria-label={isAuthenticated ? `Open ${displayName}'s profile` : 'Sign in'}
                >
                  {isAuthenticated ? (
                    <>
                      <Avatar name={displayName} url={photoUrl} size="sm" />
                      <span className="truncate max-w-[200px]">{displayName}</span>
                    </>
                  ) : (
                    <>
                      <div className="bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center shrink-0" aria-hidden="true">
                        <User size={16} className="text-slate-600" />
                      </div>
                      <span>Sign in</span>
                    </>
                  )}
                </button>
              )}

            </div>
          </div>
        </>
      )}

    </header>
  );
};
